<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="Microsoft Peering向けNAT設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{
          natSetting: natSetting,
          newIp: newIp,
        }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <!--VPN/VNNコード-->
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <app-label-group
                      label="VPN/VNコード"
                      :value="natSettingDetails.vpnVnCode"
                    />
                  </v-col>
                </v-row>

                <!--NAT用グローバルIP-->
                <v-row>
                  <v-col cols="12">
                    <app-label-group label="NAT用グローバルIP">
                      <template #value>
                        <div class="ml-3">
                          現在 {{ natSettingDetails.usedNat }}個 / 上限
                          {{ natSettingDetails.upperLimitNat }}個
                        </div>
                        <div class="ml-3">
                          追加グローバルIP数 : {{ newIp.length }} 個
                        </div>
                      </template>
                    </app-label-group>

                    <v-table density="compact" class="border-less mt-5">
                      <thead>
                        <tr>
                          <th class="font-weight-bold">
                            <app-badge
                              item="グローバルIP"
                              :isInfo="false"
                              :isRequired="false"
                            ></app-badge>
                          </th>
                          <th class="font-weight-bold">
                            <app-badge
                              item="プライベートIP"
                              :msg="
                                '・' +
                                $filter.message('info_cidr_ip_v4_with_subnet', {
                                  subnet: '32',
                                }) +
                                '\n' +
                                '・' +
                                $filter.message('info_private_ip_subnet', {
                                  subnet: '32',
                                })
                              "
                              :isRequired="displayRequired"
                            />
                          </th>
                          <th class="font-weight-bold">
                            <app-badge
                              item="備考"
                              msg="20文字以内"
                              :isRequired="false"
                            ></app-badge>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- 払出済みグローバルIP情報 -->
                        <validation-array
                          name="natSetting"
                          v-slot="{ fields, remove }"
                        >
                          <tr
                            v-for="(item, index) in fields"
                            :key="item.key + 'paid'"
                          >
                            <td class="d-flex align-center">
                              {{ natSetting[index].globalIpAddress }}
                            </td>
                            <td>
                              <validation-provider
                                :name="`ip_${item.key}`"
                                label="プライベートIP"
                                v-model="natSetting[index].privateIpAddress"
                                :rules="{
                                  required: displayRequired,
                                  is_ip_subnet: { eq: 32 },
                                  is_not_current_ip: true,
                                  is_not_loopback_ip: true,
                                  is_not_multicast_ip: true,
                                  is_not_class_e_ip: true,
                                  is_not_broadcast_ip: true,
                                  duplicate: { list: privateIpList(index) },
                                }"
                                v-slot="{ componentField, errorMessage, meta }"
                              >
                                <app-form-input
                                  :disabled="canNotEdit"
                                  v-bind="componentField"
                                  :error-messages="errorMessage"
                                  :field-meta="meta"
                                  :placeholder="
                                    canNotEdit
                                      ? null
                                      : $filter.message(
                                          'placeholder_ip_address_with_subnet'
                                        )
                                  "
                                />
                              </validation-provider>
                            </td>
                            <td class="v-col-5 pa-0">
                              <validation-provider
                                label="備考"
                                :name="`description_${item.key}`"
                                v-model="natSetting[index].description"
                                :rules="{ max: { length: 20 } }"
                                v-slot="{ componentField, errorMessage, meta }"
                              >
                                <app-form-input
                                  v-bind="componentField"
                                  :error-messages="errorMessage"
                                  :field-meta="meta"
                                  :disabled="canNotEdit"
                                />
                              </validation-provider>
                            </td>
                            <td class="d-flex align-center">
                              <v-btn
                                icon="$mdiMinus"
                                size="small"
                                variant="outlined"
                                @click="removeNat(index, remove)"
                                :disabled="isEdited"
                                class="rounded-sm"
                              >
                              </v-btn>
                            </td>
                          </tr>
                        </validation-array>

                        <!-- 払い出していないグローバルIP情報 -->
                        <validation-array
                          name="newIp"
                          v-slot="{ fields, remove, push }"
                        >
                          <tr
                            v-for="(item, index) in fields"
                            :key="item.key + 'new'"
                          >
                            <td class="d-flex align-center add-global-ip-width">
                              設定完了後にIPが払い出されます
                            </td>
                            <td>
                              <validation-provider
                                :name="`ip2_${item.key}`"
                                label="プライベートIP"
                                v-model="newIp[index].privateIpAddress"
                                :rules="{
                                  required: true,
                                  is_ip_subnet: { eq: 32 },
                                  is_not_current_ip: true,
                                  is_not_loopback_ip: true,
                                  is_not_multicast_ip: true,
                                  is_not_class_e_ip: true,
                                  is_not_broadcast_ip: true,
                                  duplicate: {
                                    list: privateIpList(
                                      index + natSetting.length
                                    ),
                                  },
                                }"
                                v-slot="{ componentField, errorMessage, meta }"
                              >
                                <app-form-input
                                  v-bind="componentField"
                                  :error-messages="errorMessage"
                                  :field-meta="meta"
                                  :placeholder="
                                    $filter.message(
                                      'placeholder_ip_address_with_subnet32'
                                    )
                                  "
                                />
                              </validation-provider>
                            </td>
                            <td class="v-col-4 pa-0">
                              <validation-provider
                                :name="`description2_${item.key}`"
                                label="備考"
                                v-model="newIp[index].description"
                                :rules="{ max: { length: 20 } }"
                                v-slot="{ componentField, errorMessage, meta }"
                              >
                                <app-form-input
                                  v-bind="componentField"
                                  :error-messages="errorMessage"
                                  :field-meta="meta"
                                />
                              </validation-provider>
                            </td>
                            <td
                              class="d-flex align-center"
                              v-if="
                                !(
                                  natSettingDetails.usedNat === 0 &&
                                  newIp.length === 1
                                )
                              "
                            >
                              <v-btn
                                icon="$mdiMinus"
                                size="small"
                                variant="outlined"
                                @click="removeNewIp(index, remove)"
                                class="rounded-sm"
                              >
                              </v-btn>
                            </td>
                            <td v-else></td>
                          </tr>
                          <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                              <v-btn
                                icon="$mdiPlus"
                                size="small"
                                variant="outlined"
                                class="rounded-sm"
                                :disabled="!canAdd || isEdited"
                                @click="addNewIp(push)"
                              />
                            </td>
                          </tr>
                        </validation-array>
                      </tbody>
                    </v-table>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">設定</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import cloneDeep from "lodash/cloneDeep";
import type { NatIpEntity, NatNaptDetailEntity } from "@/apis/MSPeeringIpApi";
import MSNatSettingModifyConfirm from "@/modals/msNatSetting/MSNatSettingModifyConfirm.vue";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "MSNatSettingModify",
  props: {
    /** NAT用IP設定情報 */
    natSettingDetails: {
      type: Object as PropType<NatNaptDetailEntity>,
      required: true,
    },
    /** 設定変更時、使用する排他キー */
    msPeeringIpKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      natSetting: cloneDeep(this.natSettingDetails.msNatIpList),
      deletedIp: [] as NatIpEntity[],
      newIp:
        this.natSettingDetails.usedNat === 0
          ? [
              {
                privateIpAddress: "",
                description: "",
              },
            ]
          : [],
    };
  },
  computed: {
    /** 払出済みグローバルIP情報:プライベートIPが1件以上の場合は、必須項目と扱われ必須表示をする */
    displayRequired(): boolean {
      return this.natSetting.length + this.newIp.length > 0;
    },
    /** プライベートIPアドレスの重複チェック用リスト作成 */
    privateIpList() {
      return (index: number) => {
        return [...this.natSetting, ...this.newIp]
          .map((e) => e.privateIpAddress)
          .filter((_, num) => num !== index);
      };
    },
    /** true: 変更不可, false: 変更可能 */
    canNotEdit(): boolean {
      return (
        this.newIp.length > 0 ||
        (this.newIp.length === 0 &&
          this.natSetting.length !== this.natSettingDetails.msNatIpList.length)
      );
    },
    /** true: 追加可能, false: 追加不可 */
    canAdd(): boolean {
      return (
        this.natSettingDetails.msNatIpList.length + this.newIp.length <
        this.natSettingDetails.upperLimitNat
      );
    },
    /** NAT用グルーバルIP追加/削除・払出済みIP設定変更操作の活性・非活性
     * true: 変更箇所有り, false: 変更箇所無し
     */
    isEdited(): boolean {
      const orgPaidIp = this.natSettingDetails.msNatIpList;
      return this.natSetting.length === orgPaidIp.length &&
        this.natSetting.find((v: NatIpEntity, i: number) => {
          return (
            v.privateIpAddress !== orgPaidIp[i].privateIpAddress ||
            v.description !== orgPaidIp[i].description
          );
        })
        ? true
        : false;
    },
  },
  methods: {
    /** NATグローバルIP削除処理(払出ずみ) */
    removeNat(index: number, remove: (idx: number) => void) {
      const deleted = this.natSetting.splice(index, 1);
      this.deletedIp = [...this.deletedIp, ...deleted];
      // ValidationにFieldを削除することを伝える
      remove(index);
    },
    /** NAT用グローバルIP追加処理 */
    addNewIp(push: (item: any) => void) {
      const addItem = {
        privateIpAddress: "",
        description: "",
      };
      this.newIp = [...this.newIp, addItem];
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },
    /** NATグローバルIP削除処理(払い出してないIP) */
    removeNewIp(index: number, remove: (idx: number) => void) {
      this.newIp.splice(index, 1);
      // ValidationにFieldを削除することを伝える
      remove(index);
    },

    async submit() {
      await this.$modal.show(MSNatSettingModifyConfirm, {
        natSetting: this.natSetting,
        currentInfo: this.natSettingDetails,
        newIp: this.newIp,
      });

      if (!this.isEdited) {
        /** 払出済みグローバルIP設定削除 + グローバルIP追加 */
        await this.$api.msPeeringIp.updateGlobalIp(
          this.natSettingDetails.msServiceSeq,
          {
            addNapt: null,
            addNat: this.newIp.length,
            msNatAddPrivateIpList: this.newIp.map((e) => ({
              privateIpAddress: e.privateIpAddress,
              description: e.description,
            })),
            deleteMsNaptGlobalIpList: null,
            deleteMsNatGlobalIpList: this.deletedIp.map(
              (e) => e.cloudGlobalIpAddressSeq
            ),
            msPeeringIpKeyId: this.msPeeringIpKeyId,
          }
        );
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      } else {
        /** 払出済みグローバルIP設定変更 */
        await this.$api.msPeeringIp.addNat(
          this.natSettingDetails.msServiceSeq,
          {
            msNatSettingList: this.natSetting.map((e) => ({
              cloudGlobalIpAddressSeq: e.cloudGlobalIpAddressSeq,
              privateIpAddress: e.privateIpAddress,
              description: e.description,
            })),
            msPeeringIpKeyId: this.msPeeringIpKeyId,
          }
        );
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      }
    },
  },
});
</script>
<style lang="scss" scoped>
.add-global-ip-width {
  min-width: 255px;
}
.modal-body {
  min-height: 55vh;
}
</style>
